import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Key, 
  ChevronLeft, 
  ChevronRight,
  LogOut,
  Settings,
  CreditCard,
  Home,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../../contexts/SupabaseAuthContext';

interface AdminSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const { signOut } = useAuth();

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/users', icon: Users, label: 'Usuários' },
    { path: '/admin/licenses', icon: Key, label: 'Licenças' },
    { path: '/admin/sales', icon: CreditCard, label: 'Vendas' },
    { path: '/admin/notification-queue', icon: MessageSquare, label: 'Fila de Mensagens' },
    { path: '/admin/whatsapp-test', icon: Settings, label: 'Config. WhatsApp' },
    { path: '/', icon: Home, label: 'Voltar para o Site' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <motion.div 
      initial={{ width: isCollapsed ? 80 : 250 }}
      animate={{ width: isCollapsed ? 80 : 250 }}
      transition={{ duration: 0.3, type: 'spring', stiffness: 200, damping: 25 }}
      className="h-screen bg-black/90 border-r border-white/10 flex flex-col fixed left-0 top-0 z-50 backdrop-blur-xl"
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-white/10 h-16">
        {!isCollapsed && (
          <motion.span 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-white font-bold text-lg tracking-wider"
          >
            OVERPIXEL
            <span className="text-yellow-500 text-xs ml-1 align-top">ADMIN</span>
          </motion.span>
        )}
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors ml-auto"
        >
          {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Menu */}
      <div className="flex-1 py-6 px-3 space-y-2">
        {menuItems.map((item) => (
          <Link to={item.path} key={item.path}>
            <div 
              className={`
                flex items-center p-3 rounded-xl cursor-pointer transition-all duration-200 group
                ${isActive(item.path) 
                  ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 border border-yellow-500/20' 
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'}
              `}
            >
              <item.icon size={22} className={`${isActive(item.path) ? 'text-yellow-400' : 'group-hover:text-white'} min-w-[22px]`} />
              
              {!isCollapsed && (
                <motion.span 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="ml-3 font-medium whitespace-nowrap"
                >
                  {item.label}
                </motion.span>
              )}
              
              {isActive(item.path) && !isCollapsed && (
                <motion.div 
                  layoutId="activeIndicator"
                  className="ml-auto w-1.5 h-1.5 rounded-full bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.5)]"
                />
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-white/10">
        <button 
          onClick={() => signOut()}
          className="w-full flex items-center p-3 rounded-xl text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200"
        >
          <LogOut size={22} className="min-w-[22px]" />
          {!isCollapsed && (
            <motion.span 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="ml-3 font-medium"
            >
              Sair
            </motion.span>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default AdminSidebar;
